import { useCallback, useState } from 'react';
import { EditOutlined } from '@ant-design/icons';
import { Divider, Typography } from 'antd';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useProcContext } from '@/entities/proc';
import { ProcActions } from '@/pages/proc/ProcActions';
import { useRouterContext } from '@/shared/router';
import { themeConfig, useThemeContext } from '@/shared/theme';
import { Row } from '@/shared/ui';
import { PageLayoutHeaderGoBackBtn } from '@/widgets/page-layout';
import { UnsavedChangesModal } from '@/widgets/proc-edit-view';

const { Title } = Typography;

type HeaderProps = {
  procTitle?: string;
  openDrawer?: () => void;
};

export const EditProcPageHeader = ({ procTitle, openDrawer }: HeaderProps) => {
  const {
    proc,
    changesSaved,
    setChangesSaved,
    resetLogicCount,
    setResetLogicCount,
  } = useProcContext();
  const navigate = useNavigate();
  const { theme } = useThemeContext();
  const { dashboardGoBackLink } = useRouterContext();

  const [isHovering, setIsHovering] = useState(false);
  const [unsavedChangesGoBackModalOpen, setUnsavedChangesGoBackModalOpen] =
    useState(false);

  const handleGoBack = useCallback(() => {
    if (changesSaved) {
      navigate(dashboardGoBackLink);
    } else {
      setUnsavedChangesGoBackModalOpen(true);
    }
  }, [changesSaved, dashboardGoBackLink, navigate]);

  return (
    <HeaderWrapper>
      <PageLayoutHeaderGoBackBtn
        onClick={handleGoBack}
        label="Go back to dashboard"
      />
      <TitleWrapper
        data-testid="proc-title"
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
        onClick={() => (openDrawer ? openDrawer() : null)}
      >
        <Title
          level={5}
          ellipsis={{ rows: 2, expandable: false }}
          style={{
            fontWeight: 400,
            textOverflow: 'ellipsis',
            lineHeight: '1.1',
            overflow: 'hidden',
            margin: 0,
          }}
        >
          {procTitle || 'My new proc'}
        </Title>
        <EditOutlined
          style={{
            color: themeConfig[theme].colors.text,
            opacity: isHovering ? 0.7 : 1,
            transition: 'opacity 0.2s ease-in-out',
          }}
        />
      </TitleWrapper>
      <Row gap={0} style={{ width: 'auto' }}>
        <ProcActions
          proc={proc}
          changesSaved={changesSaved}
          setChangesSaved={setChangesSaved}
          resetLogicCount={resetLogicCount}
          setResetLogicCount={setResetLogicCount}
        />
        <Divider
          type="vertical"
          style={{
            fontSize: 35,
            borderInlineStart: `1px solid ${themeConfig[theme].colors.border}`,
          }}
        />
      </Row>
      <UnsavedChangesModal
        open={unsavedChangesGoBackModalOpen}
        onConfirm={() => navigate('/dashboard')}
        onCancel={() => setUnsavedChangesGoBackModalOpen(false)}
      />
    </HeaderWrapper>
  );
};

const HeaderWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const TitleWrapper = styled.div`
  width: 60%;
  padding-inline: 30px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
`;
